import { useEffect, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Loader2, X } from 'lucide-react';
import { ETAGE_STATUTS, createEtageSchema, type CreateEtageInput } from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';

/**
 * Modal CRUD étage (US-BAT-03). Champs Form Builder déférés.
 */
export function EtageFormModal({
  etageId,
  batimentId,
  onClose,
  onSaved,
}: {
  etageId?: string;
  batimentId?: string;
  onClose(): void;
  onSaved(): void;
}) {
  const isEdit = !!etageId;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateEtageInput>({
    resolver: zodResolver(createEtageSchema) as unknown as Resolver<CreateEtageInput>,
    defaultValues: {
      batiment_id: batimentId ?? '',
      statut: 'operationnel',
    },
  });

  useEffect(() => {
    if (!etageId) return;
    (async () => {
      try {
        const res = await api.get<{ data: CreateEtageInput }>(`/etages/${etageId}`);
        reset({
          batiment_id: res.data.data.batiment_id,
          code: res.data.data.code,
          numero: res.data.data.numero,
          nom: res.data.data.nom ?? '',
          surface_totale: res.data.data.surface_totale ?? undefined,
          statut: res.data.data.statut,
          responsable_id: res.data.data.responsable_id ?? undefined,
        });
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [etageId, reset]);

  async function onSubmit(values: CreateEtageInput) {
    setSaving(true);
    setErrorMessage(null);
    try {
      const payload = {
        ...values,
        nom: values.nom || null,
        surface_totale: values.surface_totale || null,
      };
      if (isEdit) {
        await api.patch(`/etages/${etageId}`, payload);
      } else {
        await api.post('/etages', payload);
      }
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!etageId) return;
    if (!confirm('Supprimer cet étage ? (soft delete — uniquement si aucune salle rattachée)'))
      return;
    setSaving(true);
    try {
      await api.delete(`/etages/${etageId}`);
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-xl w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? "Modifier l'étage" : 'Nouvel étage'}
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {errorMessage && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              </div>
            ) : (
              <form id="etage-form" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                <input type="hidden" {...register('batiment_id')} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Code *" error={errors.code?.message}>
                    <input {...register('code')} placeholder="ET-RDC" className={inputClass} />
                  </Field>
                  <Field label="Numéro *" error={errors.numero?.message}>
                    <input {...register('numero')} placeholder="RDC, R+1…" className={inputClass} />
                  </Field>
                  <Field label="Nom" error={errors.nom?.message} className="col-span-2">
                    <input {...register('nom')} placeholder="ex. Accueil" className={inputClass} />
                  </Field>
                  <Field label="Surface totale (m²)" error={errors.surface_totale?.message}>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      {...register('surface_totale', {
                        setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                      })}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Statut *" error={errors.statut?.message}>
                    <select {...register('statut')} className={inputClass}>
                      {ETAGE_STATUTS.map((s) => (
                        <option key={s} value={s}>
                          {s.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </form>
            )}
          </div>

          <div className="flex items-center justify-between p-4 border-t border-gray-200 flex-shrink-0">
            <div>
              {isEdit && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                  disabled={saving}
                >
                  Supprimer
                </button>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                form="etage-form"
                disabled={saving || loading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isEdit ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({
  label,
  error,
  children,
  className,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
